import { useEffect, useState } from 'react';
import { settingsApi } from '@/features/ai-dart/api/settings';
import { ticketsApi } from '@/features/ai-dart/api/tickets';
import type { Repo, Ticket } from '@/features/ai-dart/types';
import { ActionButton } from '@/features/ai-dart/components/controls';

type Resolution = 'fixed' | 'wontfix' | 'duplicate';
/** 서버가 허용하는 값과 그 뜻 — 자유 입력으로 받지 않는다. */
const RESOLUTIONS: Array<{ value: Resolution; label: string }> = [
  { value: 'fixed', label: 'fixed (해결됨)' },
  { value: 'wontfix', label: 'wontfix (안 고침)' },
  { value: 'duplicate', label: 'duplicate (중복)' },
];
const SELECT = 'cursor-pointer rounded-md border border-line bg-surface px-2 py-1 text-[11px] transition hover:border-accent-line focus:border-accent focus:outline-none disabled:cursor-not-allowed disabled:opacity-50';

export function TicketActions({ ticket, onChanged }: { ticket: Ticket; onChanged: () => void }): React.JSX.Element {
  const [repos, setRepos] = useState<Repo[]>([]);
  const [repo, setRepo] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState(false);
  const [resolution, setResolution] = useState<Resolution>('wontfix');
  const attributable = !ticket.repo && !['closed', 'noise', 'autofix_running'].includes(ticket.status);
  useEffect(() => { if (attributable) settingsApi.repos.list().then(setRepos).catch(() => setRepos([])); }, [attributable]);

  const run = async (work: () => Promise<unknown>, success: string): Promise<void> => {
    setBusy(true); setMessage('');
    try { await work(); if (success) setMessage(success); onChanged(); } catch (error) { setMessage((error as Error).message); }
    finally { setBusy(false); }
  };
  const parked = ['needs_human', 'failed', 'interrupted'].includes(ticket.status);
  const orphaned = ticket.status === 'autofix_running'
    && (!ticket.leaseExpiresAt || new Date(ticket.leaseExpiresAt).getTime() < Date.now());
  const restartable = parked || orphaned;
  const closable = ['failed', 'interrupted'].includes(ticket.status) || (ticket.status === 'needs_human' && !ticket.prUrl);
  const prMustSettle = ticket.status === 'pr_ready' || (ticket.status === 'needs_human' && Boolean(ticket.prUrl));

  /**
   * 종결은 대화상자를 띄우지 않고 **버튼 자리에서 select를 펼친다**(현행 dart-ui와 같은 방식).
   * `window.prompt`는 사유를 자유 입력으로 받아 오타가 그대로 서버 400이 되고, 어떤 값이 허용되는지
   * 화면이 알려주지 못한다.
   */
  const close = (): void => void run(
    () => ticketsApi.act(ticket.id, { action: 'close', resolution }),
    '티켓을 종결했습니다.',
  ).then(() => setClosing(false));
  /** 잡을 기동해 브랜치·PR을 만든다 — 비가역이고 과금된다. 설정 화면의 즉시 실행과 같은 무게다. */
  const dispatch = (): void => {
    if (!window.confirm(`#${ticket.id}을(를) 지금 처리합니다.\n자동수정 잡이 즉시 기동해 ${ticket.repo}에 브랜치·PR을 만들 수 있고 중간에 되돌릴 수 없습니다. 진행할까요?`)) return;
    void run(() => ticketsApi.dispatch(ticket.id), '단건 처리를 요청했습니다.');
  };
  const attribute = (): void => void run(async () => {
    const result = await ticketsApi.act(ticket.id, { action: 'attribute', repo });
    setMessage(result.auto === 'allow'
      ? "자동수정은 재발 횟수가 임계값에 이를 때만 걸립니다 — 지금 태우려면 '지금 처리'를 누르세요."
      : "자동수정도 '지금 처리'도 거부됩니다 — 설정 > 레포/브랜치에서 allow로 바꾸세요.");
  }, '');

  return (
    <section className="rounded-lg border border-line bg-surface p-4">
      <div className="mb-3 text-[11px] font-semibold tracking-wide text-muted uppercase">사람 조치</div>
      <div className="flex flex-wrap items-center gap-2">
        {restartable && (
          <ActionButton disabled={busy} onClick={() => void run(() => ticketsApi.act(ticket.id, { action: 'reopen' }), '다시 시도 요청을 반영했습니다.')}>
            {orphaned ? '멈춤 재시작' : '다시 시도'}
          </ActionButton>
        )}
        {closable && !closing && (
          <ActionButton tone="danger" disabled={busy} onClick={() => setClosing(true)}>종결…</ActionButton>
        )}
        {closable && closing && (
          <>
            <select
              value={resolution}
              disabled={busy}
              autoFocus
              onChange={(event) => setResolution(event.target.value as Resolution)}
              className={SELECT}
            >
              {RESOLUTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
            <ActionButton tone="danger" disabled={busy} onClick={close}>{busy ? '종결 중…' : '종결 확정'}</ActionButton>
            <ActionButton disabled={busy} onClick={() => setClosing(false)}>취소</ActionButton>
          </>
        )}
        {ticket.status === 'open' && ticket.repo && (
          <ActionButton tone="primary" disabled={busy} title="자동수정 잡을 즉시 기동합니다 — 되돌릴 수 없습니다" onClick={dispatch}>
            {busy ? '요청 중…' : '지금 처리'}
          </ActionButton>
        )}
        {attributable && (
          <>
            <select
              value={repo}
              disabled={busy}
              onChange={(event) => setRepo(event.target.value)}
              className={SELECT}
            >
              <option value="">레포 선택</option>
              {repos.map((item) => <option key={item.id} value={item.repo}>{item.repo} ({item.auto})</option>)}
            </select>
            <ActionButton tone="primary" disabled={busy || !repo} onClick={attribute}>귀속 지정</ActionButton>
          </>
        )}
      </div>
      {prMustSettle && <p className="mt-3 text-[12px] text-amber-700">PR을 먼저 정리하세요 — PR이 닫히면 종결할 수 있습니다.</p>}
      {orphaned && <p className="mt-3 text-[11.5px] leading-relaxed text-muted">멈춤 재시작은 만료된 lease를 해제하고 open으로 돌려 다음 dispatch가 다시 처리하게 합니다.</p>}
      {message && <p className="mt-3 rounded-md bg-line-soft px-3 py-2 text-[12px] leading-relaxed">{message}</p>}
    </section>
  );
}
